
import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Database, CheckCircle, AlertTriangle, XCircle, Lock, Cloud, CloudOff } from 'lucide-react';
import { storageModeManager, StorageModeStatus } from '@/services/storage/storageModeManager';
import { universalStorage } from '@/services/storage/universalStorage';
import { dbService } from '@/services/db';
import { certificateTemplateSupabaseService } from '@/services/certification/supabaseTemplateService';
import { universalSettingsService } from '@/services/settings/universalSettingsService';
import { reportsService } from '@/services/db/reports';

export const StorageStatusIndicator = () => {
  const [status, setStatus] = useState<StorageModeStatus | null>(null);
  const [storageInfo, setStorageInfo] = useState<any>(null);
  const [timetableSyncStatus, setTimetableSyncStatus] = useState<'synced' | 'pending' | 'offline'>('offline');
  const [certificateSyncStatus, setCertificateSyncStatus] = useState<'synced' | 'pending' | 'offline'>('offline');
  const [settingsSyncStatus, setSettingsSyncStatus] = useState<'synced' | 'pending' | 'offline'>('offline');
  const [reportsSyncStatus, setReportsSyncStatus] = useState<'synced' | 'pending' | 'offline'>('offline');

  useEffect(() => {
    const updateStatus = async () => {
      const currentStatus = await storageModeManager.getStorageStatus();
      const info = await universalStorage.getStorageInfo();
      const timetableStatus = dbService.getTimetableSyncStatus();
      const certificateStatus = certificateTemplateSupabaseService.getSyncStatus();
      const settingsStatus = universalSettingsService.getSupabaseSyncStatus();
      const reportsStatus = reportsService.getSupabaseSyncStatus();
      
      setStatus(currentStatus);
      setStorageInfo(info);
      setTimetableSyncStatus(timetableStatus);
      setCertificateSyncStatus(certificateStatus);
      setSettingsSyncStatus(settingsStatus);
      setReportsSyncStatus(reportsStatus);
    };

    updateStatus();
    
    // Update every 10 seconds
    const interval = setInterval(updateStatus, 10000);
    
    return () => clearInterval(interval);
  }, []);

  if (!status) return null;

  const getHealthIcon = () => {
    switch (status.healthStatus) {
      case 'healthy':
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-3 w-3 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-3 w-3 text-red-500" />;
    }
  };

  const getStorageIcon = () => {
    // Show cloud icon when any Supabase sync is available (synced or pending)
    if (timetableSyncStatus === 'synced' || timetableSyncStatus === 'pending' || 
        certificateSyncStatus === 'synced' || certificateSyncStatus === 'pending' ||
        settingsSyncStatus === 'synced' || settingsSyncStatus === 'pending' ||
        reportsSyncStatus === 'synced' || reportsSyncStatus === 'pending') {
      return <Cloud className="h-5 w-5 text-white" />;
    }
    // Show lock icon when offline (localStorage only)
    return <Lock className="h-5 w-5 text-white" />;
  };

  const getStorageColor = () => {
    // Green when all are synced, blue when any is pending, original green when offline
    if ((timetableSyncStatus === 'synced' || timetableSyncStatus === 'offline') && 
        (certificateSyncStatus === 'synced' || certificateSyncStatus === 'offline') &&
        (settingsSyncStatus === 'synced' || settingsSyncStatus === 'offline') &&
        (reportsSyncStatus === 'synced' || reportsSyncStatus === 'offline')) {
      return 'bg-green-500';
    } else if (timetableSyncStatus === 'pending' || certificateSyncStatus === 'pending' || 
               settingsSyncStatus === 'pending' || reportsSyncStatus === 'pending') {
      return 'bg-blue-500';
    }
    return 'bg-green-500'; // Default
  };

  const isSupabaseActive = timetableSyncStatus !== 'offline' || certificateSyncStatus !== 'offline' || 
                          settingsSyncStatus !== 'offline' || reportsSyncStatus !== 'offline';

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`w-12 h-12 rounded-full ${getStorageColor()} flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-all duration-200 border-2 border-white dark:border-gray-800`}>
            {getStorageIcon()}
          </div>
        </div>
      </HoverCardTrigger>
      
      <HoverCardContent 
        className="w-64 bg-background/95 backdrop-blur-sm border shadow-lg" 
        side="left" 
        sideOffset={10}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span className="text-sm font-medium">Storage</span>
            </div>
            <div className="flex items-center gap-1">
              {isSupabaseActive ? (
                <Cloud className="h-3 w-3 text-blue-500" />
              ) : (
                <Lock className="h-3 w-3 text-green-500" />
              )}
              {getHealthIcon()}
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <Badge variant="default" className="text-xs">
              localStorage
            </Badge>
            <span className="text-xs text-muted-foreground">
              primary
            </span>
          </div>

          {/* Show Supabase status when available */}
          {isSupabaseActive && (
            <>
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-xs border-blue-500 text-blue-800">
                  Supabase
                </Badge>
                <span className="text-xs text-muted-foreground">
                  hybrid sync
                </span>
              </div>
              
              {/* Timetable sync status */}
              {timetableSyncStatus !== 'offline' && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Timetables:</span>
                  <span className={timetableSyncStatus === 'synced' ? 'text-green-600' : 'text-blue-600'}>
                    {timetableSyncStatus}
                  </span>
                </div>
              )}
              
              {/* Certificate sync status */}
              {certificateSyncStatus !== 'offline' && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Certificates:</span>
                  <span className={certificateSyncStatus === 'synced' ? 'text-green-600' : 'text-blue-600'}>
                    {certificateSyncStatus}
                  </span>
                </div>
              )}

              {/* Settings sync status */}
              {settingsSyncStatus !== 'offline' && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Settings:</span>
                  <span className={settingsSyncStatus === 'synced' ? 'text-green-600' : 'text-blue-600'}>
                    {settingsSyncStatus}
                  </span>
                </div>
              )}

              {/* Reports sync status */}
              {reportsSyncStatus !== 'offline' && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Reports:</span>
                  <span className={reportsSyncStatus === 'synced' ? 'text-green-600' : 'text-blue-600'}>
                    {reportsSyncStatus}
                  </span>
                </div>
              )}
            </>
          )}
          
          <div className={`text-xs flex items-center gap-1 ${
            isSupabaseActive ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'
          }`}>
            {isSupabaseActive ? (
              <>
                <Cloud className="h-3 w-3" />
                Hybrid storage active
              </>
            ) : (
              <>
                <CheckCircle className="h-3 w-3" />
                Always enabled
              </>
            )}
          </div>
          
          {storageInfo && (
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">
                Data: {storageInfo.keysCount} keys
              </div>
              <div className="text-xs text-muted-foreground">
                Size: {storageInfo.totalSize}
              </div>
              <div className="text-xs text-muted-foreground">
                Backups: {storageInfo.backupCount}
              </div>
            </div>
          )}
          
          <div className="text-xs text-muted-foreground">
            Last verified: {new Date(status.lastVerified).toLocaleTimeString()}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
